/**
 * OccupancyClassificationSelect — the occupancy classification picker, backed by
 * the org's effective offered vocabulary (place_occupancy_classifications with
 * org prefs applied, via the Places API).
 *
 * Replaces the former live-dictionary `BedesField` binding (boundary doctrine,
 * docs/BEDES.md: the runtime never reads the BEDES dictionary — vocabularies are
 * materialized Places tables seeded from the hub). Stored values remain the
 * canonical hub spellings; the R1 FK on place_uses guarantees membership.
 */
import { useMemo } from "react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useOccupancyClassifications } from "@/hooks/usePlaces";

interface OccupancyClassificationSelectProps {
  /** The stored canonical value, e.g. "Office". */
  value: string | null | undefined;
  /** Called with the canonical value, or `null` when cleared. */
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function OccupancyClassificationSelect({
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: OccupancyClassificationSelectProps) {
  const { data: classifications } = useOccupancyClassifications();

  const options = useMemo(() => {
    const offered = (classifications ?? []).map((c) => ({
      value: c.value,
      label: c.value,
    }));
    // A stored value the org has since hidden (or a legacy value) must remain
    // visible/selectable on its own row rather than silently blanking.
    if (value && !offered.some((o) => o.value === value)) {
      return [{ value, label: value }, ...offered];
    }
    return offered;
  }, [classifications, value]);

  return (
    <SearchableSelect
      options={options}
      value={value ?? undefined}
      // SearchableSelect emits "" on deselect; persist null so optional columns
      // clear cleanly.
      onValueChange={(v) => onChange(v === "" ? null : v)}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}
